import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, Zap, Clock, Target, ShoppingCart, Crown } from 'lucide-react';
import { useEnergyStore } from '@/stores/energyStore';
import { useVibeCoinStore } from '@/stores/vibecoinStore';
import { useState } from 'react';
import './energy.css';

export default function NoEnergyModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    showNoEnergyModal, noEnergyAction, noEnergyCost, energy, maxEnergy,
    closeNoEnergyModal, purchaseEnergy, fetchStatus, timeToNextRegen,
  } = useEnergyStore();
  const { balance: vbcBalance, fetchBalance } = useVibeCoinStore();
  const [purchasing, setPurchasing] = useState(false);

  if (!showNoEnergyModal) return null;

  const msToRegen = timeToNextRegen();
  const minutesToRegen = Math.ceil(msToRegen / 60000);

  const handlePurchase = async () => {
    setPurchasing(true);
    const ok = await purchaseEnergy(10); // minimum: 10 VBC = 5 energy
    if (ok) {
      await fetchStatus();
      await fetchBalance();
      closeNoEnergyModal();
    }
    setPurchasing(false);
  };

  return (
    <div className="energy-modal-overlay" onClick={closeNoEnergyModal}>
      <div className="energy-modal" onClick={e => e.stopPropagation()}>
        <button className="energy-modal-close" onClick={closeNoEnergyModal}>
          <X size={18} />
        </button>

        {/* Energy bar */}
        <div className="energy-modal-bar-wrap">
          <Zap size={24} className="energy-modal-bolt" />
          <div className="energy-modal-bar">
            <div
              className="energy-modal-bar-fill"
              style={{ width: `${Math.max(2, (energy / maxEnergy) * 100)}%` }}
            />
          </div>
          <span className="energy-modal-bar-text">{energy}/{maxEnergy}</span>
        </div>

        <h3 className="energy-modal-title">{t('energy.noEnergy.title')}</h3>
        <p className="energy-modal-subtitle">
          {t('energy.noEnergy.subtitle', { cost: noEnergyCost, current: energy })}
        </p>

        {/* Options */}
        <div className="energy-modal-options">
          {/* 1. Wait for regen */}
          {msToRegen > 0 && (
            <div className="energy-modal-option">
              <Clock size={18} className="energy-modal-option-icon" style={{ color: '#60a5fa' }} />
              <div>
                <strong>{t('energy.noEnergy.wait')}</strong>
                <p>{t('energy.noEnergy.waitDesc', { minutes: minutesToRegen })}</p>
              </div>
            </div>
          )}

          {/* 2. Complete missions */}
          <button className="energy-modal-option energy-modal-option--btn" onClick={() => { closeNoEnergyModal(); navigate('/odisseia'); }}>
            <Target size={18} className="energy-modal-option-icon" style={{ color: '#4ade80' }} />
            <div>
              <strong>{t('energy.noEnergy.missions')}</strong>
              <p>{t('energy.noEnergy.missionsDesc')}</p>
            </div>
          </button>

          {/* 3. Buy with VibeCoin */}
          <button
            className="energy-modal-option energy-modal-option--btn energy-modal-option--gold"
            onClick={handlePurchase}
            disabled={purchasing || vbcBalance < 10}
          >
            <ShoppingCart size={18} className="energy-modal-option-icon" style={{ color: '#fbbf24' }} />
            <div>
              <strong>{t('energy.noEnergy.purchase')}</strong>
              <p>{t('energy.noEnergy.purchaseDesc', { vbc: 10, energy: 5, balance: vbcBalance })}</p>
            </div>
          </button>

          {/* 4. Subscribe */}
          <button className="energy-modal-option energy-modal-option--btn energy-modal-option--premium" onClick={() => { closeNoEnergyModal(); navigate('/store'); }}>
            <Crown size={18} className="energy-modal-option-icon" style={{ color: '#c084fc' }} />
            <div>
              <strong>{t('energy.noEnergy.subscribe')}</strong>
              <p>{t('energy.noEnergy.subscribeDesc')}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
